import { createSlice } from "@reduxjs/toolkit";
import {
  createRendezvous,
  updateRendezvous,
  cancelRendezvous,
  confirmRendezvous,
  rescheduleRendezvous,
  fetchAllRendezvous,
  fetchRendezvousByMedecin,
  fetchPatientRendezvousByExternalId,
  fetchConfirmedRendezvous,
  fetchScheduledRendezvous,
  fetchRescheduledRendezvous,
} from "../thunks/rendezvousThunks";

const initialState = {
  creating: false,
  createError: null,
  lastCreated: null,

  updating: false,
  updateError: null,

  cancelling: false,
  cancelError: null,

  confirming: false,
  confirmError: null,

  rescheduling: false,
  rescheduleError: null,

  fetching: false,
  fetchError: null,
  items: [],

  reminder: null,

  byMedecin:   { loading: false, error: null, items: [] },
  byPatient:   { loading: false, error: null, items: [] },
  confirmed:   { loading: false, error: null, items: [] },
  scheduled:   { loading: false, error: null, items: [] },
  rescheduled: { loading: false, error: null, items: [] },
};

/* ── helpers ── */
const pickId = (x) => {
  if (!x) return null;
  const id = x.id ?? x._id ?? x.uuid ?? x.externalId;
  return id == null ? null : String(id);
};

const mergeUpdated = (oldItem, updated) => {
  if (!updated || typeof updated !== "object") return oldItem;
  return { ...oldItem, ...updated };
};

const applyUpdate = (list, rid, updated) => {
  const idx = list.findIndex((r) => String(r?.id ?? r?._id) === String(rid));
  if (idx !== -1) list[idx] = mergeUpdated(list[idx], updated);
};

const applyUpdateToAllLists = (state, rid, updated) => {
  applyUpdate(state.items,              rid, updated);
  applyUpdate(state.byMedecin.items,   rid, updated);
  applyUpdate(state.byPatient.items,   rid, updated);
  applyUpdate(state.confirmed.items,   rid, updated);
  applyUpdate(state.scheduled.items,   rid, updated);
  applyUpdate(state.rescheduled.items, rid, updated);
};

const toArray = (payload) =>
  Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

/* ── slice ── */
const rendezvousSlice = createSlice({
  name: "rendezvous",
  initialState,
  reducers: {
    clearCreateError(state)    { state.createError    = null; },
    clearFetchError(state)     { state.fetchError     = null; },
    clearRescheduleError(state){ state.rescheduleError = null; },
    clearLastCreated(state)    { state.lastCreated    = null; },

    clearAllErrors(state) {
      state.createError     = null;
      state.updateError     = null;
      state.cancelError     = null;
      state.confirmError    = null;
      state.rescheduleError = null;
      state.fetchError      = null;
      state.byMedecin.error   = null;
      state.byPatient.error   = null;
      state.confirmed.error   = null;
      state.scheduled.error   = null;
      state.rescheduled.error = null;
    },

    setRendezvousReminder(state, action)  { state.reminder = action.payload || null; },
    clearRendezvousReminder(state)        { state.reminder = null; },

    addLocalRdv(state, action) {
      const rdv = action.payload;
      if (rdv && rdv.id) {
        // Add to global items
        state.items.push(rdv);
        // Add to byMedecin so CalendarGrid sees it immediately
        state.byMedecin.items.push(rdv);
      }
    },
  },

  extraReducers: (builder) => {

    /* ================= CREATE ================= */
    builder
      .addCase(createRendezvous.pending, (s) => {
        s.creating = true;
        s.createError = null;
      })
      .addCase(createRendezvous.fulfilled, (s, a) => {
        s.creating = false;
        s.lastCreated = a.payload;
        const created = a.payload?.data ?? a.payload;
        if (created) {
          const rid = pickId(created);

          // Add to global items list
          const exists = s.items.some((x) => pickId(x) === rid);
          if (!exists) s.items.push(created);

          // Also add to byMedecin so CalendarGrid sees it immediately
          const existsByMedecin = s.byMedecin.items.some((x) => pickId(x) === rid);
          if (!existsByMedecin) s.byMedecin.items.push(created);

          // Also add to byPatient if patient is viewing
          const existsByPatient = s.byPatient.items.some((x) => pickId(x) === rid);
          if (!existsByPatient) s.byPatient.items.push(created);
        }
      })
      .addCase(createRendezvous.rejected, (s, a) => {
        s.creating = false;
        s.createError = a.payload?.message || a.error?.message || "Failed to create rendezvous";
      });

    /* ================= UPDATE ================= */
    builder
      .addCase(updateRendezvous.pending, (s) => {
        s.updating = true;
        s.updateError = null;
      })
      .addCase(updateRendezvous.fulfilled, (s, a) => {
        s.updating = false;
        const updated = a.payload?.data ?? a.payload;
        const rid = pickId(updated);
        if (rid) applyUpdateToAllLists(s, rid, updated);
      })
      .addCase(updateRendezvous.rejected, (s, a) => {
        s.updating = false;
        s.updateError = a.payload?.message || a.error?.message || "Failed to update rendezvous";
      });

    /* ================= CANCEL ================= */
    builder
      .addCase(cancelRendezvous.pending, (s) => {
        s.cancelling = true;
        s.cancelError = null;
      })
      .addCase(cancelRendezvous.fulfilled, (s, a) => {
        s.cancelling = false;
        const rid         = a.payload?.id;
        const updated     = a.payload?.updated;
        const cancelledBy = a.payload?.cancelledBy || null;
        if (!rid) return;

        const normalized =
          updated && typeof updated === "object"
            ? { ...updated, status: updated.status ?? "cancelled", ...(cancelledBy ? { cancelledBy } : {}) }
            : { status: "cancelled",                                ...(cancelledBy ? { cancelledBy } : {}) };

        applyUpdateToAllLists(s, rid, normalized);

        // retirer des scheduled
        s.scheduled.items = s.scheduled.items.filter(
          (r) => String(r?.id ?? r?._id) !== String(rid)
        );
      })
      .addCase(cancelRendezvous.rejected, (s, a) => {
        s.cancelling = false;
        s.cancelError = a.payload?.message || a.error?.message || "Cancel failed";
      });

    /* ================= CONFIRM ================= */
    builder
      .addCase(confirmRendezvous.pending, (s) => {
        s.confirming = true;
        s.confirmError = null;
      })
      .addCase(confirmRendezvous.fulfilled, (s, a) => {
        s.confirming = false;
        const rid     = a.payload?.id;
        const updated = a.payload?.data ?? a.payload?.updated;
        if (!rid) return;
        const normalized =
          updated && typeof updated === "object"
            ? { ...updated, status: updated.status ?? "confirmed" }
            : { status: "confirmed" };
        applyUpdateToAllLists(s, rid, normalized);
      })
      .addCase(confirmRendezvous.rejected, (s, a) => {
        s.confirming = false;
        s.confirmError = a.payload?.message || a.error?.message || "Failed to confirm rendezvous";
      });

    /* ================= RESCHEDULE ================= */
    builder
      .addCase(rescheduleRendezvous.pending, (s) => {
        s.rescheduling = true;
        s.rescheduleError = null;
      })
      .addCase(rescheduleRendezvous.fulfilled, (s, a) => {
        s.rescheduling = false;
        const updated = a.payload?.data ?? a.payload;
        const rid = pickId(updated);
        if (!rid) return;
        const normalized =
          updated && typeof updated === "object"
            ? { ...updated, status: updated.status ?? "rescheduled" }
            : { status: "rescheduled" };
        applyUpdateToAllLists(s, rid, normalized);
      })
      .addCase(rescheduleRendezvous.rejected, (s, a) => {
        s.rescheduling = false;
        s.rescheduleError = a.payload?.message || a.error?.message || "Failed to reschedule rendezvous";
      });

    /* ================= FETCH ALL ================= */
    builder
      .addCase(fetchAllRendezvous.pending, (s) => {
        s.fetching = true;
        s.fetchError = null;
      })
      .addCase(fetchAllRendezvous.fulfilled, (s, a) => {
        s.fetching = false;
        s.items = toArray(a.payload);
      })
      .addCase(fetchAllRendezvous.rejected, (s, a) => {
        s.fetching = false;
        s.fetchError = a.payload?.message || a.error?.message || "Failed to fetch rendezvous";
      });

    /* ================= BY MEDECIN ================= */
    builder
      .addCase(fetchRendezvousByMedecin.pending, (s) => {
        s.byMedecin.loading = true;
        s.byMedecin.error   = null;
      })
      .addCase(fetchRendezvousByMedecin.fulfilled, (s, a) => {
        s.byMedecin.loading = false;
        s.byMedecin.items   = Array.isArray(a.payload) ? a.payload : [];
      })
      .addCase(fetchRendezvousByMedecin.rejected, (s, a) => {
        s.byMedecin.loading = false;
        s.byMedecin.error   = a.payload?.message || a.error?.message || "Failed to fetch by medecin";
      });

    /* ================= BY PATIENT ================= */
    builder
      .addCase(fetchPatientRendezvousByExternalId.pending, (s) => {
        s.byPatient.loading = true;
        s.byPatient.error   = null;
      })
      .addCase(fetchPatientRendezvousByExternalId.fulfilled, (s, a) => {
        s.byPatient.loading = false;
        s.byPatient.items   = Array.isArray(a.payload) ? a.payload : [];
      })
      .addCase(fetchPatientRendezvousByExternalId.rejected, (s, a) => {
        s.byPatient.loading = false;
        s.byPatient.error   = a.payload?.message || a.error?.message || "Failed to fetch by patient";
      });

    /* ================= CONFIRMED ================= */
    builder
      .addCase(fetchConfirmedRendezvous.pending, (s) => {
        s.confirmed.loading = true;
        s.confirmed.error   = null;
      })
      .addCase(fetchConfirmedRendezvous.fulfilled, (s, a) => {
        s.confirmed.loading = false;
        s.confirmed.items   = toArray(a.payload);
      })
      .addCase(fetchConfirmedRendezvous.rejected, (s, a) => {
        s.confirmed.loading = false;
        s.confirmed.error   = a.payload?.message || a.error?.message || "Failed to fetch confirmed";
      });

    /* ================= SCHEDULED ================= */
    builder
      .addCase(fetchScheduledRendezvous.pending, (s) => {
        s.scheduled.loading = true;
        s.scheduled.error   = null;
      })
      .addCase(fetchScheduledRendezvous.fulfilled, (s, a) => {
        s.scheduled.loading = false;
        s.scheduled.items   = toArray(a.payload);
      })
      .addCase(fetchScheduledRendezvous.rejected, (s, a) => {
        s.scheduled.loading = false;
        s.scheduled.error   = a.payload?.message || a.error?.message || "Failed to fetch scheduled";
      });

    /* ================= RESCHEDULED ================= */
    builder
      .addCase(fetchRescheduledRendezvous.pending, (s) => {
        s.rescheduled.loading = true;
        s.rescheduled.error   = null;
      })
      .addCase(fetchRescheduledRendezvous.fulfilled, (s, a) => {
        s.rescheduled.loading = false;
        s.rescheduled.items   = toArray(a.payload);
      })
      .addCase(fetchRescheduledRendezvous.rejected, (s, a) => {
        s.rescheduled.loading = false;
        s.rescheduled.error   = a.payload?.message || a.error?.message || "Failed to fetch rescheduled";
      });
  },
});

export const {
  clearCreateError,
  clearFetchError,
  clearRescheduleError,
  clearAllErrors,
  clearLastCreated,
  setRendezvousReminder,
  clearRendezvousReminder,
  addLocalRdv,
} = rendezvousSlice.actions;

export const selectRendezvousReminder = (s) => s?.rendezvous?.reminder ?? null;

export default rendezvousSlice.reducer;